import * as React from "react";
import { Link, useHistory } from "react-router-dom";
import { useCurrentStudyConfigurationContext } from "./CurrentStudyConfigurationContext";
import { useSecurityContext } from "./SecurityContext";
import { Study } from "webportalclient";
import { Authorization, GlobalAuthorization } from "../misc/components/Authorization";
import { PermissionEnum } from "../misc/api/ApiPermissionEnum";
import { useAuth } from "react-oidc-context";
import { useWebPortalLegacyClient } from "../misc/api/WebPortalTokenClient";
import { ExtractBase64Image } from "../Administration/Studies/StudyForm";
import { useReferenceContextProvider } from "./ReferenceContextProvider";
import { SaveCurrentPath } from "../Core/index";
import { AlertNotificationPopout } from "../alert-notification/AlertNotificationPopout";
import { useAboutContextProvider } from "./AboutContextProvider";

declare const rootUrl: string;
declare const window: any;

interface TopNavbarUserMenuProps {
    isMultiStudy: boolean;
    currentStudy: Study;
}

export const TopNavbarUserMenu = ({ isMultiStudy, currentStudy }: TopNavbarUserMenuProps) => {
    const { user, signoutRedirect } = useAuth();
    const { context, currentUserPermissions } = useSecurityContext();
    const { version, logoutRedirectUri } = useReferenceContextProvider();
    const legacyClient = useWebPortalLegacyClient();
    const [studyLogo, setStudyLogo] = React.useState<string>();
    const history = useHistory();
    const { about } = useAboutContextProvider();

    React.useEffect(() => {
        const getLogo = async () => {
            if (currentStudy?.logo) {
                const studyLogoResult = await legacyClient.getStudyLogo(currentStudy.id);
                const base64Logo = await ExtractBase64Image(studyLogoResult)
                setStudyLogo(base64Logo);
            }
        }

        getLogo();
    }, [user, currentStudy]);

    const signOut = async () => {
        await legacyClient.LogSignInAuditTrail(true);
        signoutRedirect({ post_logout_redirect_uri: `${rootUrl}${logoutRedirectUri}` });
    }

    return (
        <ul className="nav navbar-top-links navbar-nav navbar-right">
            {
                about.SunriseEnabled ?
                    <li>
                        <Link id="sunriseLink" to={{ pathname: about.SunriseUri }} role="button" target="_blank">{about.SunriseClientLinkLabel}</Link>
                    </li>
                    :
                    <></>
            }
            <GlobalAuthorization currentPermissions={currentUserPermissions} permissionId={PermissionEnum.AccessAdmin}>
                <li>
                    <Link id="adminMenu" to="/admin" role="button"><span className="fas fa-cog fa-lg" style={{ marginRight: 2 + 'px' }}></span></Link>
                </li>
            </GlobalAuthorization>
            <li className="dropdown" >
                <a id="supportMenu" role="button" className="dropdown-toggle" data-toggle="dropdown"><span className="fas fa-question-circle fa-lg" style={{ marginRight: 2 + 'px' }}></span><span className="caret"></span></a>
                <ul role="menu" className="dropdown-menu">
                    <li><a href="https://wphelp.vitalograph-clinical.info/" target="_blank" rel="noreferrer">Help Documentation</a></li>
                    <li><Link to="/support/documents">Support Documents</Link></li>

                    <Authorization studyId={currentStudy?.id} currentPermissions={currentUserPermissions} permissionId={PermissionEnum.ViewMappedServerDirectories}>
                        <li><Link to="/sharedfiles">Shared Files</Link></li>
                    </Authorization>

                    <li><Link to="/support/details">Support contact details</Link></li>
                </ul>
            </li>
            {!isMultiStudy ? <TopNavbarUserMenuStudySpecific /> : null}
            <li className="dropdown">
                <a id="userMenu" role="button" className="dropdown-toggle" data-toggle="dropdown" data-automationid="UserNavBar">
                    {user !== undefined && user !== null ? (<>
                        <img src={user.profile.picture} alt="User logo" height="20" width="20" />
                        <span style={{ fontWeight: 'normal' }}>{user.profile.name}&nbsp;&nbsp;&#8226;&nbsp;&nbsp;</span>
                    </>)
                        : <i className="fas fa-user-circle fa-lg" style={{ marginRight: 2 + 'px' }}></i>}
                    {
                        (currentStudy?.logo && studyLogo ? <img src={`data:${currentStudy.logoMediaType};base64,${studyLogo}`} alt="Study logo" height="20" />
                            : <span style={{ fontWeight: 'normal' }}>{currentStudy?.name ?? "All Studies"}</span>)
                    }
                    <span className="caret"></span>
                </a>
                <ul role="menu" className="dropdown-menu" style={{ minWidth: 210 + 'px' }}>
                    <li><Link to="/account/settings">Account settings</Link></li>
                    <li><a onClick={() => { SaveCurrentPath(history); history.push("/account/study") }}>Switch study</a></li>
                    <li><a data-automationid="LogOutMenuButton" onClick={signOut}>Sign out</a></li>
                    <li role="separator" className="divider"></li>

                    <li className="navbar-text">
                        <span data-toggle="tooltip" data-placement="top">
                            {version}
                        </span>
                    </li>
                </ul>
            </li>
        </ul>
    );
}

export const TopNavbarUserMenuStudySpecific = () => {
    const { study } = useCurrentStudyConfigurationContext();

    return (
        <>
            <AlertNotificationPopout currentStudyId={study.id} />
        </>
    );
}
